import { clamp } from "common-utils/utils/arithmetic";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import ModalV2 from "kreate-common/modules/kreate-ui/components/ModalV2";
import React from "react";

import FigureMintingImage from "../ModalProgress/components/FigureMintingImage";

import styles from "./index.module.scss";

type Duration = number; // milliseconds
type Ratio = number; // 0.0 .. 1.0

type Props = {
  open: boolean;
  onClose?: () => void;
  ratio: [Ratio, Ratio];
  expectedDuration: Duration;
  refreshInterval?: Duration;
  content?: React.ReactNode;
  children?: React.ReactNode;
};

export default function ModalProgressV2({
  open,
  onClose,
  ratio,
  expectedDuration,
  refreshInterval = 500,
  content,
  children = content,
}: Props) {
  const [ratioStart, ratioEnd] = ratio;
  const [ratioCurr, setRatioCurr] = React.useState(ratioStart);

  React.useEffect(() => {
    setRatioCurr((value) => clamp(ratioStart, value, ratioEnd));

    const id = setInterval(() => {
      // https://en.wikipedia.org/wiki/Exponential_decay
      const decayConstant = 1 / expectedDuration;
      const shrinkRatioAfterOneInterval = Math.pow(
        1 - decayConstant,
        refreshInterval
      );
      setRatioCurr(
        (value) => ratioEnd - (ratioEnd - value) * shrinkRatioAfterOneInterval
      );
    }, refreshInterval);

    return () => clearInterval(id);
  }, [expectedDuration, ratioEnd, ratioStart, refreshInterval]);

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      closeOnDimmerClick={false}
      closeOnEscape={false}
    >
      <ModalV2.Content>
        <Flex.Col
          minHeight="292px"
          alignItems="center"
          justifyContent="center"
          gap="32px"
        >
          <FigureMintingImage />
          <div className={styles.content}>{children}</div>
          <div className={styles.progressBarContainer}>
            <div
              className={styles.progressBarCompletion}
              style={
                {
                  "--ratio": ratioCurr,
                  "--interval": `${refreshInterval}ms`,
                } as React.CSSProperties
              }
            ></div>
          </div>
        </Flex.Col>
      </ModalV2.Content>
    </ModalV2>
  );
}
